<template>
    <div class="metrics-tab">
        <!-- 수렴 지표 -->
        <b-card no-body class="p-3 mb-4">
            <div class="d-flex flex-wrap align-items-end mb-3">
                <b-form-group label="캠페인 ID" label-for="metrics-campaign" class="mr-2 mb-0">
                    <b-form-input
                        id="metrics-campaign"
                        v-model="filters.campaign_id"
                        type="number"
                        placeholder="전체"
                        style="width: 130px"
                    />
                </b-form-group>
                <b-form-group label="기업 ID" label-for="metrics-company" class="mr-2 mb-0">
                    <b-form-input
                        id="metrics-company"
                        v-model="filters.company_id"
                        type="number"
                        placeholder="전체"
                        style="width: 130px"
                    />
                </b-form-group>
                <b-form-group label="최근 잡 수" label-for="metrics-limit" class="mr-2 mb-0">
                    <b-form-input
                        id="metrics-limit"
                        v-model="filters.limit"
                        type="number"
                        placeholder="50"
                        style="width: 110px"
                    />
                </b-form-group>
                <b-button variant="primary" :disabled="loading" @click="search">조회</b-button>
            </div>

            <b-row v-if="overall" class="mb-3">
                <b-col cols="3">
                    <div class="metric-card">
                        <div class="metric-label">전체 일치율</div>
                        <div class="metric-value">{{ formatRate(overall.agreement_rate) }}</div>
                    </div>
                </b-col>
                <b-col cols="3">
                    <div class="metric-card">
                        <div class="metric-label">대상 잡</div>
                        <div class="metric-value">{{ overall.jobs }}</div>
                    </div>
                </b-col>
                <b-col cols="3">
                    <div class="metric-card">
                        <div class="metric-label">판정 문항</div>
                        <div class="metric-value">{{ overall.items_judged }}</div>
                    </div>
                </b-col>
                <b-col cols="3">
                    <div class="metric-card">
                        <div class="metric-label">평가자가 뒤집은 문항</div>
                        <div class="metric-value">{{ overall.items_overridden }}</div>
                    </div>
                </b-col>
            </b-row>

            <p class="text-muted small">
                일치율 = 1 − (평가자가 뒤집은 문항 / 판정 완료 문항). 1.0 에 수렴할수록
                사람 검토를 표본 검토로 줄일 근거가 됩니다.
            </p>

            <b-table
                :items="jobs"
                :fields="jobFields"
                :busy="loading"
                hover
                responsive
                show-empty
            >
                <template #empty>
                    <PageEmptyComponent
                        compact
                        :error="loadError ? '수렴 지표를 불러오지 못했습니다.' : ''"
                        text="완료된 잡이 없습니다."
                        @retry="fetchMetrics"
                    />
                </template>

                <template #cell(scope)="row">
                    <div class="small text-muted">
                        <div>캠페인 {{ row.item.campaign_id || '-' }}</div>
                        <div>기업 {{ row.item.company_id || '-' }}</div>
                    </div>
                </template>

                <template #cell(finished_at)="row">
                    {{ formatDateTime(row.item.finished_at) }}
                </template>

                <template #cell(agreement_rate)="row">
                    <div class="d-flex align-items-center">
                        <b-progress
                            :value="(row.item.agreement_rate || 0) * 100"
                            :variant="rateVariant(row.item.agreement_rate)"
                            class="flex-grow-1 mr-2"
                            style="height: 8px"
                        />
                        <span style="width: 56px">{{ formatRate(row.item.agreement_rate) }}</span>
                    </div>
                </template>

                <template #cell(actions)="row">
                    <b-button size="sm" variant="outline-secondary" @click="requestGate(row.item.job_id)">
                        게이트 조회
                    </b-button>
                </template>
            </b-table>
        </b-card>

        <!-- 골든 게이트 -->
        <b-card id="golden-gate-card" no-body class="p-3">
            <div class="d-flex align-items-end mb-3">
                <b-form-group label="골든 게이트 조회 (잡 ID)" label-for="gate-job-id" class="mr-2 mb-0">
                    <b-form-input
                        id="gate-job-id"
                        v-model="gateJobId"
                        type="number"
                        placeholder="예: 30"
                        style="width: 160px"
                        @keyup.enter="requestGate()"
                    />
                </b-form-group>
                <b-button variant="primary" :disabled="gateLoading" @click="requestGate()">
                    <b-spinner v-if="gateLoading" small class="mr-1" />
                    조회
                </b-button>
            </div>

            <p class="text-muted small">
                프롬프트·판례 개정을 활성화하기 전, 같은 캠페인×기업으로 새로 완주시킨 섀도 잡의
                AI 원판정을 골든셋과 대조합니다.
            </p>

            <b-alert :show="!!gateError" variant="danger" class="mb-0">{{ gateError }}</b-alert>

            <template v-if="gate">
                <div class="d-flex align-items-center mb-3">
                    <b-badge :variant="gateVariant" class="gate-badge mr-3">{{ gateLabel }}</b-badge>
                    <span class="text-muted">
                        잡 #{{ gate.job_id }} · 캠페인 {{ gate.campaign_id }} · 기업 {{ gate.company_id }}
                    </span>
                </div>

                <b-row class="mb-3">
                    <b-col cols="3">
                        <div class="metric-card">
                            <div class="metric-label">골든 케이스</div>
                            <div class="metric-value">{{ gate.golden_total }}</div>
                        </div>
                    </b-col>
                    <b-col cols="3">
                        <div class="metric-card">
                            <div class="metric-label">평가 대상</div>
                            <div class="metric-value">{{ gate.evaluated }}</div>
                        </div>
                    </b-col>
                    <b-col cols="3">
                        <div class="metric-card">
                            <div class="metric-label">일치</div>
                            <div class="metric-value">{{ gate.matches }}</div>
                        </div>
                    </b-col>
                    <b-col cols="3">
                        <div class="metric-card">
                            <div class="metric-label">일치율</div>
                            <div class="metric-value">{{ formatRate(gate.agreement_rate) }}</div>
                        </div>
                    </b-col>
                </b-row>

                <div v-if="gate.mismatches && gate.mismatches.length" class="mb-3">
                    <strong class="text-danger">불일치 {{ gate.mismatches.length }}건</strong>
                    <b-table
                        :items="gate.mismatches"
                        :fields="mismatchFields"
                        small
                        responsive
                        class="mt-2"
                    >
                        <template #cell(expected)="row">
                            <b-badge variant="light">{{ judgementLabel(row.item.expected) }}</b-badge>
                        </template>
                        <template #cell(got)="row">
                            <b-badge variant="danger">{{ judgementLabel(row.item.got) }}</b-badge>
                        </template>
                    </b-table>
                </div>

                <div v-if="gate.review_required && gate.review_required.length" class="mb-2">
                    <span class="text-muted mr-2">
                        판정 유보 {{ gate.review_required.length }}건 (실패 아님) —
                    </span>
                    <b-badge
                        v-for="code in gate.review_required"
                        :key="`rev-${code}`"
                        variant="secondary"
                        class="mr-1"
                    >
                        {{ code }}
                    </b-badge>
                </div>

                <div v-if="gate.stale && gate.stale.length" class="mb-2">
                    <span class="text-muted mr-2">
                        답변행 교체로 제외 {{ gate.stale.length }}건 —
                    </span>
                    <b-badge v-for="code in gate.stale" :key="`stale-${code}`" variant="light" class="mr-1">
                        {{ code }}
                    </b-badge>
                </div>

                <div v-if="gate.missing && gate.missing.length" class="mb-2">
                    <span class="text-muted mr-2">
                        섀도 잡에 없는 문항 {{ gate.missing.length }}건 —
                    </span>
                    <b-badge v-for="code in gate.missing" :key="`miss-${code}`" variant="light" class="mr-1">
                        {{ code }}
                    </b-badge>
                </div>
            </template>
        </b-card>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {
    formatDateTime,
    formatRate,
    judgementLabel,
    queryPositiveInt,
    sameQuery,
} from './prompt-labels';

// URL 에 남기는 조회 조건(?campaign_id=&company_id=&limit=) — 값은 양의 정수만, 빈 값은 생략.
const FILTER_KEYS = ['campaign_id', 'company_id', 'limit'];

export default {
    name: 'MetricsTab',
    components: {PageEmptyComponent},
    data() {
        return {
            loading: false,
            loadError: false,
            fetchReq: 0,
            jobs: [],
            overall: null,
            // 입력 중인 값(filters)과 마지막으로 조회한 조건(applied)을 나눈다 — URL 에는 조회한 조건만 남긴다.
            filters: {campaign_id: '', company_id: '', limit: ''},
            applied: {campaign_id: null, company_id: null, limit: null},
            gateJobId: '',
            appliedGateJob: null,
            gateReq: 0,
            gate: null,
            gateError: '',
            gateLoading: false,
            jobFields: [
                {key: 'job_id', label: '잡', thStyle: {width: '80px'}},
                {key: 'scope', label: '대상', thStyle: {width: '120px'}},
                {key: 'finished_at', label: '완료', thStyle: {width: '140px'}},
                {key: 'items_judged', label: '판정', thStyle: {width: '80px'}},
                {key: 'items_overridden', label: '뒤집힘', thStyle: {width: '80px'}},
                {key: 'agreement_rate', label: '일치율'},
                {key: 'actions', label: '', thStyle: {width: '110px'}},
            ],
            mismatchFields: [
                {key: 'sub_code', label: '문항'},
                {key: 'expected', label: '평가자 확정'},
                {key: 'got', label: 'AI 원판정'},
            ],
        };
    },
    computed: {
        // gate_pass 는 3-state — 평가 대상이 0건이면 백엔드가 null(판정 불가)을 준다.
        gateVariant() {
            if (!this.gate) return 'secondary';
            if (this.gate.gate_pass === true) return 'success';
            if (this.gate.gate_pass === false) return 'danger';
            return 'secondary';
        },
        gateLabel() {
            if (!this.gate) return '';
            if (this.gate.gate_pass === true) return '게이트 통과';
            if (this.gate.gate_pass === false) return '게이트 실패';
            return '판정 불가 (평가 대상 0건)';
        },
    },
    watch: {
        // 뒤로가기 등으로 같은 화면의 URL 이 바뀌면 조건·게이트를 맞춘다(화면이 직접 바꾼 URL 은 이미 같은 상태).
        '$route.query'(query) {
            this.applyQuery(query);
        },
    },
    created() {
        this.applyQuery(this.$route.query, true);
    },
    methods: {
        formatDateTime,
        formatRate,
        judgementLabel,
        applyQuery(query, force = false) {
            const next = {};
            FILTER_KEYS.forEach((k) => { next[k] = queryPositiveInt(query[k]); });
            if (force || FILTER_KEYS.some((k) => next[k] !== this.applied[k])) {
                FILTER_KEYS.forEach((k) => {
                    this.applied[k] = next[k];
                    this.filters[k] = next[k] === null ? '' : String(next[k]);
                });
                this.fetchMetrics();
            }
            const gateJob = queryPositiveInt(query.gate_job);
            if (force || gateJob !== this.appliedGateJob) {
                this.appliedGateJob = gateJob;
                this.gateJobId = gateJob === null ? '' : String(gateJob);
                // 골든 게이트는 조회(GET)만 한다.
                if (gateJob) {
                    this.checkGate();
                } else {
                    this.gateReq += 1;  // 진행 중이던 조회 결과는 버린다
                    this.gate = null;
                    this.gateError = '';
                    this.gateLoading = false;
                }
            }
        },
        // 조회한 조건(applied)과 게이트 잡만 URL 에 남긴다. 사용자 조작이라 push.
        syncQuery() {
            const query = {...this.$route.query};
            [...FILTER_KEYS, 'gate_job'].forEach((k) => { delete query[k]; });
            FILTER_KEYS.forEach((k) => {
                if (this.applied[k]) query[k] = String(this.applied[k]);
            });
            if (this.appliedGateJob) query.gate_job = String(this.appliedGateJob);
            if (!sameQuery(query, this.$route.query)) this.$router.push({query}).catch(() => {});
        },
        // [조회] — 입력값 중 양의 정수만 조건으로 확정한다(그 밖의 값은 비운다).
        search() {
            FILTER_KEYS.forEach((k) => {
                this.applied[k] = queryPositiveInt(this.filters[k]);
                this.filters[k] = this.applied[k] === null ? '' : String(this.applied[k]);
            });
            this.syncQuery();
            this.fetchMetrics();
        },
        async fetchMetrics() {
            const req = ++this.fetchReq;  // 연달아 조회했을 때 늦게 온 이전 응답은 버린다
            this.loading = true;
            this.loadError = false;
            try {
                const params = {};
                FILTER_KEYS.forEach((k) => {
                    if (this.applied[k]) params[k] = this.applied[k];
                });
                const {data} = await this.$axios.get('/supplement-prompt/metrics', {params});
                if (req !== this.fetchReq) return;
                this.jobs = (data && data.jobs) || [];
                this.overall = (data && data.overall) || null;
            } catch (e) {
                if (req !== this.fetchReq) return;
                // 다른 조건의 지표가 남아 보이지 않도록 비우고 '없음' 대신 오류를 보인다.
                this.jobs = [];
                this.overall = null;
                this.loadError = true;
                if (e?.response?.status === 401) return;
                this.$bvToast.toast(getErrorMessage(e, '수렴 지표를 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                if (req === this.fetchReq) this.loading = false;
            }
        },
        // [조회]·Enter·행의 [게이트 조회] — 잡 ID 를 확정하고 URL 에 남긴 뒤 조회한다.
        requestGate(jobId) {
            // 잡 목록에서 호출한 경우 결과 카드가 화면 밖에 있으므로 함께 스크롤한다.
            // b-card 는 functional 컴포넌트라 ref 가 잡히지 않아 id 로 찾는다.
            if (jobId !== undefined && jobId !== null) {
                this.gateJobId = String(jobId);
                this.$nextTick(() => {
                    const el = this.$el && this.$el.querySelector('#golden-gate-card');
                    if (el && el.scrollIntoView) el.scrollIntoView({behavior: 'smooth', block: 'start'});
                });
            }
            const id = queryPositiveInt(this.gateJobId);
            if (!id) {
                this.gateError = '잡 ID를 입력해 주세요.';
                this.gate = null;
                return;
            }
            this.appliedGateJob = id;
            this.syncQuery();
            this.checkGate();
        },
        async checkGate() {
            const req = ++this.gateReq;  // 늦게 온 이전 게이트 응답이 최신 결과를 덮지 않게 한다
            this.gateLoading = true;
            this.gateError = '';
            try {
                const {data} = await this.$axios.get('/supplement-prompt/golden-gate', {
                    params: {job_id: this.appliedGateJob},
                });
                if (req !== this.gateReq) return;
                this.gate = data;
            } catch (e) {
                if (req !== this.gateReq) return;
                this.gate = null;
                const status = (e.response && e.response.status) || 0;
                if (status === 409) {
                    this.gateError = getErrorMessage(e, '완료된 잡만 게이트 대조가 가능합니다.');
                } else if (status === 404) {
                    this.gateError = getErrorMessage(e, '해당 잡을 찾을 수 없습니다.');
                } else {
                    this.gateError = getErrorMessage(e, '골든 게이트 조회에 실패했습니다.');
                }
            } finally {
                if (req === this.gateReq) this.gateLoading = false;
            }
        },
        rateVariant(rate) {
            if (rate === null || rate === undefined) return 'secondary';
            if (rate >= 0.9) return 'success';
            if (rate >= 0.7) return 'warning';
            return 'danger';
        },
    },
};
</script>

<style scoped>
.metric-card {
    border: 1px solid #eaeaea;
    border-radius: 0.25rem;
    padding: 0.75rem 1rem;
}

.metric-label {
    font-size: 0.85rem;
    color: #888;
}

.metric-value {
    font-size: 1.5rem;
    font-weight: 700;
}

.gate-badge {
    font-size: 1rem;
    padding: 0.5rem 0.9rem;
}
</style>
